import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Users() {
  const [users, setUsers] = useState({
    users: [],
    total: 0,
    page: 1,
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [type, setType] = useState('email');
  const [hasExpoPushToken, setHasExpoPushToken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [actionUserId, setActionUserId] = useState(null);
  const [trialModal, setTrialModal] = useState({ open: false, userId: null, email: '' });
  const [trialCount, setTrialCount] = useState('');
  const [trialMode, setTrialMode] = useState('add');
  const [subscriptionModal, setSubscriptionModal] = useState({ open: false, userId: null, email: '' });
  const [subscriptionStatus, setSubscriptionStatus] = useState('premium');
  const [subscriptionPlan, setSubscriptionPlan] = useState('1-month');
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('adminToken');

  const fetchUsers = async (page = 1, query = '') => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        `https://awriter-backend-si7g.onrender.com/api/admin/users?page=${page}&limit=10${query ? `&email=${encodeURIComponent(query)}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers({
        users: response.data.users || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
      });
    } catch (error) {
      console.error('Error fetching users:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to fetch users');
      setUsers({ users: [], total: 0, page: 1, limit: 10 });
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserExpoPushToken = async (userId) => {
    if (!userId) {
      setHasExpoPushToken(false);
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        `https://awriter-backend-si7g.onrender.com/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('User Expo Push Token Response:', response.data);
      setHasExpoPushToken(!!response.data.user.expoPushToken);
    } catch (error) {
      console.error('Error fetching user Expo push token:', error.message, error.response?.data);
      setHasExpoPushToken(false);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!selectedUserId) {
      setErrorMessage('Please select a user');
      return;
    }
    if (!message.trim()) {
      setErrorMessage('Message is required');
      return;
    }
    if (type === 'push' && !hasExpoPushToken) {
      setErrorMessage('Selected user does not have a registered device for push notifications');
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.post(
        'https://awriter-backend-si7g.onrender.com/api/admin/notify',
        { userId: selectedUserId, message, subject: subject.trim() || undefined, type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Send Notification Response:', response.data);
      setErrorMessage('Notification sent successfully');
      setMessage('');
      setSubject('');
      setSelectedUserId('');
      setType('email');
      setHasExpoPushToken(false);
    } catch (error) {
      console.error('Error sending notification:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to send notification');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.delete(
        `https://awriter-backend-si7g.onrender.com/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setErrorMessage(`User ${email} deleted successfully`);
      fetchUsers(users.page, searchQuery);
    } catch (error) {
      console.error('Error deleting user:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to delete user');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuspendUser = async (userId, email, currentStatus) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.patch(
        `https://awriter-backend-si7g.onrender.com/api/admin/users/${userId}/suspend`,
        { suspend: currentStatus !== 'suspended' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setErrorMessage(`User ${email} ${currentStatus !== 'suspended' ? 'suspended' : 'unsuspended'} successfully`);
      fetchUsers(users.page, searchQuery);
    } catch (error) {
      console.error('Error suspending/unsuspending user:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || `Failed to ${currentStatus !== 'suspended' ? 'suspend' : 'unsuspend'} user`);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenTrialModal = (userId, email) => {
    setTrialModal({ open: true, userId, email });
    setTrialCount('');
    setTrialMode('add');
  };

  const handleAddTrials = async () => {
    if (!trialModal.userId) {
      setErrorMessage('No user selected for trial update');
      return;
    }
    const trialCountNum = parseInt(trialCount, 10);
    if (isNaN(trialCountNum) || trialCountNum < 0) {
      setErrorMessage('Trial count must be a non-negative number');
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.patch(
        `https://awriter-backend-si7g.onrender.com/api/admin/users/${trialModal.userId}/trials`,
        { trialCount: trialCountNum, mode: trialMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setErrorMessage(`Trial count updated successfully for ${trialModal.email}`);
      setTrialModal({ open: false, userId: null, email: '' });
      fetchUsers(users.page, searchQuery);
    } catch (error) {
      console.error('Error updating trial count:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to update trial count');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenSubscriptionModal = (userId, email) => {
    setSubscriptionModal({ open: true, userId, email });
    setSubscriptionStatus('premium');
    setSubscriptionPlan('1-month');
  };

  const handleUpdateSubscription = async () => {
    if (!subscriptionModal.userId) {
      setErrorMessage('No user selected for subscription update');
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      await axios.patch(
        `https://awriter-backend-si7g.onrender.com/api/admin/users/${subscriptionModal.userId}/subscription`,
        { 
          subscriptionStatus,
          subscriptionPlan: subscriptionStatus === 'premium' ? subscriptionPlan : null 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setErrorMessage(`Subscription updated successfully for ${subscriptionModal.email}`);
      setSubscriptionModal({ open: false, userId: null, email: '' });
      fetchUsers(users.page, searchQuery);
    } catch (error) {
      console.error('Error updating subscription:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to update subscription');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(1, searchQuery);
  };

  const handleExportCSV = () => {
    const headers = ['ID,Email,First Name,Last Name,Subscription Status,Plan,Status,Trial Count,Created At'];
    const rows = users.users.map(user => [
      user.id,
      user.email,
      user.firstName || '',
      user.lastName || '',
      user.subscriptionStatus || 'N/A',
      user.subscriptionPlan || 'None',
      user.status || 'N/A',
      user.trialCount ?? 'N/A',
      user.createdAt ? format(new Date(user.createdAt), 'yyyy-MM-dd') : 'N/A',
    ].map(field => `"${field}"`).join(','));
    const csv = [...headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'users.csv');
  };

  useEffect(() => {
    fetchUsers();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserExpoPushToken(selectedUserId);
    } else {
      setHasExpoPushToken(false);
    }
  }, [selectedUserId]);

  const handlePageChange = (newPage) => {
    fetchUsers(newPage, searchQuery);
  };

  return (
    <div className="p-4 lg:p-0 space-y-10">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold mb-2">Users</h2>
        <p className="text-gray-600">Manage users and send individual notifications</p>
      </div>

      {/* Send Notification Form */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Send Notification to User</h3>
        <div className="bg-white p-6 rounded-lg shadow space-y-4 max-w-xl">
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Select User</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
            >
              <option value="">Select a user</option>
              {users.users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.email}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Notification Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
              disabled={!selectedUserId}
            >
              <option value="email">Email</option>
              <option value="push" disabled={!hasExpoPushToken}>
                Push {hasExpoPushToken ? '' : '(No device registered)'}
              </option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter your subject"
              className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Message</label>
            <textarea
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message to the user..."
              className="border rounded p-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
            />
          </div>
          <button
            onClick={handleSendNotification}
            className="bg-[#6a6a6a] hover:bg-[#46824e] duration-300 text-white px-4 py-2 rounded-md shadow"
            disabled={isLoading || !selectedUserId}
          >
            {isLoading ? 'Sending...' : 'Send Notification'}
          </button>
          {errorMessage && (
            <div className={`p-2 text-center rounded ${errorMessage.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {errorMessage}
            </div>
          )}
        </div>
      </div>

      {/* Users List */}
      <div>
        <h3 className="text-lg font-semibold mb-4">All Users</h3>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col lg:flex-row justify-between mb-4">
            <form onSubmit={handleSearch} className="flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email..."
                className="border rounded p-2 mr-2"
              />
              <button
                type="submit"
                className="bg-[#6a6a6a] text-white px-4 py-2 rounded hover:bg-[#46824e]"
              >
                Search
              </button>
            </form>
            <button
              onClick={handleExportCSV}
              className="bg-[#6a6a6a] text-white mt-4 lg:mt-0 px-4 py-2 rounded hover:bg-[#46824e]"
            >
              Export to CSV
            </button>
          </div>
          {isLoading ? (
            <LoadingSpinner />
          ) : errorMessage && !errorMessage.includes('successfully') ? (
            <div className="p-4 bg-red-100 text-red-700 rounded text-center">{errorMessage}</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left">Email</th>
                      <th className="border p-2 text-left">First Name</th>
                      <th className="border p-2 text-left">Last Name</th>
                      <th className="border p-2 text-left">Subscription Status</th>
                      <th className="border p-2 text-left">Plan</th>
                      <th className="border p-2 text-left">Status</th>
                      <th className="border p-2 text-left">Trial Count</th>
                      <th className="border p-2 text-left">Created At</th>
                      <th className="border p-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.users.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="border p-2 text-center">
                          No users found
                        </td>
                      </tr>
                    ) : (
                      users.users.map(user => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="border p-2">{user.email}</td>
                          <td className="border p-2">{user.firstName || 'N/A'}</td>
                          <td className="border p-2">{user.lastName || 'N/A'}</td>
                          <td className="border p-2">{user.subscriptionStatus || 'N/A'}</td>
                          <td className="border p-2">{user.subscriptionPlan || 'None'}</td>
                          <td className="border p-2">{user.status || 'N/A'}</td>
                          <td className="border p-2">{user.trialCount ?? 'N/A'}</td>
                          <td className="border p-2">
                            {user.createdAt
                              ? format(new Date(user.createdAt), 'yyyy-MM-dd')
                              : 'N/A'}
                          </td>
                          <td className="border p-2">
                            <select
                              value=""
                              onChange={(e) => {
                                const action = e.target.value;
                                if (action === 'delete') {
                                  handleDeleteUser(user.id, user.email);
                                } else if (action === 'suspend') {
                                  handleSuspendUser(user.id, user.email, user.status);
                                } else if (action === 'trials') {
                                  handleOpenTrialModal(user.id, user.email);
                                } else if (action === 'subscription') {
                                  handleOpenSubscriptionModal(user.id, user.email);
                                }
                              }}
                              className="border rounded p-1 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
                            >
                              <option value="">Select Action</option>
                              <option value="delete">Delete User</option>
                              <option value="suspend">
                                {user.status === 'suspended' ? 'Unsuspend' : 'Suspend'} User
                              </option>
                              <option value="trials">Add/Set Trials</option>
                              <option value="subscription">
                                {user.subscriptionStatus === 'premium' ? 'Downgrade to Free' : 'Upgrade to Premium'}
                              </option>
                            </select>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-between items-center mt-4">
                <div>
                  Showing {(users.page - 1) * users.limit + 1} to{' '}
                  {Math.min(users.page * users.limit, users.total)} of {users.total} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(users.page - 1)}
                    disabled={users.page === 1}
                    className="bg-[#6a6a6a] text-white px-4 py-2 rounded disabled:bg-gray-300 hover:bg-[#46824e]"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(users.page + 1)}
                    disabled={users.page * users.limit >= users.total}
                    className="bg-[#6a6a6a] text-white px-4 py-2 rounded disabled:bg-gray-300 hover:bg-[#46824e]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Trial Count Modal */}
      {trialModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Update Trials for {trialModal.email}</h3>
            <div className="space-y-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">Trial Count</label>
                <input
                  type="number"
                  value={trialCount}
                  onChange={(e) => setTrialCount(e.target.value)}
                  placeholder="Enter trial count"
                  className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
                  min="0"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">Mode</label>
                <select
                  value={trialMode}
                  onChange={(e) => setTrialMode(e.target.value)}
                  className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
                >
                  <option value="add">Add to Current Trials</option>
                  <option value="set">Set Trial Count</option>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setTrialModal({ open: false, userId: null, email: '' })}
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTrials}
                  className="bg-[#6a6a6a] text-white px-4 py-2 rounded hover:bg-[#46824e]"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Trials'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {subscriptionModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Update Subscription for {subscriptionModal.email}</h3>
            <div className="space-y-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">Subscription Status</label>
                <select
                  value={subscriptionStatus}
                  onChange={(e) => setSubscriptionStatus(e.target.value)}
                  className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
                >
                  <option value="premium">Premium</option>
                  <option value="free">Free</option>
                </select>
              </div>
              {subscriptionStatus === 'premium' && (
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Subscription Plan</label>
                  <select
                    value={subscriptionPlan}
                    onChange={(e) => setSubscriptionPlan(e.target.value)}
                    className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-[#6a6a6a]"
                  >
                    <option value="1-month">1 Month</option>
                    <option value="6-month">6 Months</option>
                    <option value="1-year">1 Year</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setSubscriptionModal({ open: false, userId: null, email: '' })}
                  className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateSubscription}
                  className="bg-[#6a6a6a] text-white px-4 py-2 rounded hover:bg-[#46824e]"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Subscription'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}